/**
 * Scroll breakpoint per hero variant: the sticky nav appears once this element
 * has scrolled out of view. Only one hero renders per page, so the first match
 * in document order is the right one.
 *
 * The project hero targets its first definition list rather than the section
 * root, because that section also wraps the themes and progress columns.
 */
const HERO_SELECTORS = [
  ".denhaag-hero--theme-image",
  ".denhaag-hero--theme-page",
  ".denhaag-project-hero__content > dl:first-of-type",
  ".denhaag-hero--carousel",
  ".denhaag-hero--home",
  ".denhaag-hero--routing",
];

export default class StickyMobileThemeNavigation {
  constructor() {
    this._isMenuOpen = false

    this.stickySubNav = null;
    this.menuToggle = null;
    this.mobileMenu = null;

    this.mobileMenuActiveClass = 'denhaag-sticky-mobile-theme-navigation--menu-active';
  }

  /**
   * Getter for isMenuOpen - this runs whenever isMenuOpen is accessed.
   * @return {boolean|*}
   */
  get isMenuOpen() {
    return this._isMenuOpen;
  }

  /**
   * Setter for isMenuOpen - this runs whenever isMenuOpen is updated.
   * @param value {boolean}
   */
  set isMenuOpen(value) {
    if (this._isMenuOpen !== value) {
      this._isMenuOpen = value;

      !this._isMenuOpen
        ? this.onOpenMenu()
        : this.onCloseMenu()
    }
  }

  /**
   * Initialize the StickyMobileThemeNavigation component.
   */
  initialize() {
    this.menuToggle = document.getElementById( 'denhaag-theme-navigation-toggle' );
    this.mobileMenu = document.getElementById( "denhaag-mobile-menu" );
    this.stickySubNav = document.getElementById( 'denhaag-sticky-mobile-theme-navigation' );
  }

  /**
   * Run the StickyMobileThemeNavigation component.
   */
  isEnabled() {
    return !!this.stickySubNav && !!this.menuToggle && !!this.mobileMenu;
  }

  events() {
    if (!this.isEnabled()) {
      return;
    }

    this.menuToggle.onclick = () => {
      this.menuToggle.getAttribute( "aria-expanded" ) === "true" ? this.onOpenMenu() : this.onCloseMenu();
      this.toggleMenu( this.menuToggle.getAttribute( 'data-toggle-panel' ) );
    }

    this.menuToggle.onkeydown = (e) => {
      const {code} = e;
      if( ['Enter', 'Space'].includes( code ) ) {
        e.preventDefault();
        this.menuToggle.getAttribute( "aria-expanded" ) === "true" ? this.onOpenMenu() : this.onCloseMenu();
        this.toggleMenu( this.menuToggle.getAttribute( 'data-toggle-panel' ) );
      }
    }
  }

  toggleMenu(id) {
    this.dispatchEvent( "mobileMenu:toggle", {
      id,
      from: "stickySubNavMobile",
    } );
  }

  onOpenMenu() {
    if (!this.menuToggle) {
      // Would be weird if no id was set, but let's be sure.
      return;
    }
    this.stickySubNav.classList.add( this.mobileMenuActiveClass );

    this.menuToggle.setAttribute( "aria-expanded", "true" );
    this.menuToggle.firstElementChild.setAttribute( "aria-hidden", "true" );
    this.menuToggle.lastElementChild.removeAttribute( "aria-hidden" );
    this.menuToggle.classList.add( 'denhaag-button--secondary-action' );
    this.menuToggle.classList.remove( 'denhaag-button--primary-action' );
  }

  onCloseMenu() {
    if (!this.menuToggle) {
      // Would be weird if no id was set, but let's be sure.
      return;
    }

    this.stickySubNav?.classList.remove( this.mobileMenuActiveClass );

    this.menuToggle.setAttribute( "aria-expanded", "false" );
    this.menuToggle.firstElementChild.removeAttribute( "aria-hidden" );
    this.menuToggle.lastElementChild.setAttribute( "aria-hidden", "true" );
    this.menuToggle.classList.remove( 'denhaag-button--secondary-action' );
    this.menuToggle.classList.add( 'denhaag-button--primary-action' );
  }

  dispatchEvent(name, data) {
    if (!name) {
      return;
    }

    window.dispatchEvent( new CustomEvent( name, {
      detail: data,
      bubbles: true
    } ) );
  }

  /**
   * Setup the Event Listeners, may only be done once!
   */
  setupEventListeners() {
    // Update the button icon when the menu is opened or closed from somewhere else.
    window.addEventListener( "mobileMenu:status", (e) => this.isMenuOpen = e?.detail?.isOpen );
    window.addEventListener( "stickySubNavMobile:close", () => this.onCloseMenu() );
    window.addEventListener( "stickySubNavMobile:open", () => this.onOpenMenu() );
  }

  /**
   * Toggle the sticky nav based on whether the hero has scrolled out of view.
   * @param breakPointEls {string|string[]} Selector(s) to use as the scroll breakpoint.
   */
  showSubNav(breakPointEls = HERO_SELECTORS) {
    if (!this.isEnabled()) {
      return;
    }

    const selector = Array.isArray( breakPointEls ) ? breakPointEls.join( ", " ) : breakPointEls;
    const breakPointEl = document.querySelector( selector );
    if (!breakPointEl) {
      return;
    }

    new IntersectionObserver( ([entry]) => {
      if (entry.boundingClientRect.bottom <= 0) {
        this.stickySubNav.classList.add( "denhaag-sticky-mobile-theme-navigation--active" );
      } else {
        this.stickySubNav.classList.remove( "denhaag-sticky-mobile-theme-navigation--active" );

        if (this.isMenuOpen) {
          this.dispatchEvent( "mobileMenu:close" );
        }
      }
    } ).observe( breakPointEl );
  }
}
